import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { RowItemsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useContext } from 'react'
import { RowItemFilters, RowItemOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { CurrentTenantContext } from '@/core/current_tenant_context'
import { formatCurrency } from '@/helpers/formatCurrency'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<RowItemsQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const { pageInfo, setOpenNew, openShow, openNew, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.rowItems.pageInfo, onIndex, basePath: '/extra-items' })
  const { currentCompany } = useContext(CurrentTenantContext)

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<RowItemFilters, RowItemOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.rowItems.nodes = dedupe([
              ...previousData.rowItems.nodes,
              ...fetchMoreResult.rowItems.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Price' },
        ]}
        rows={data.rowItems.nodes.map(rowItem => (
          {
            id: rowItem.id,
            columns: [
              rowItem.name,
              formatCurrency(currentCompany?.currency || 'GBP', rowItem.priceCents)
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />

    </>
  )
}
